/**
 * Replaceable external-editor adapters. Brunel stays the control tower;
 * the editor stays on the execution host.
 */

export const IDE_ADAPTER_KEYS = ['vscode'] as const;
export type IdeAdapterKey = (typeof IDE_ADAPTER_KEYS)[number];

export function isIdeAdapterKey(value: string): value is IdeAdapterKey {
  return (IDE_ADAPTER_KEYS as readonly string[]).includes(value);
}

export interface IdeCommandSpec {
  readonly file: string;
  readonly args: readonly string[];
}

/** Pure spec: how to detect and open a workspace. I/O belongs on the node. */
export interface IdeAdapterSpec {
  readonly key: IdeAdapterKey;
  readonly displayName: string;
  readonly detect: IdeCommandSpec;
  readonly open: IdeCommandSpec;
}

export const VS_CODE_ADAPTER: IdeAdapterSpec = {
  key: 'vscode',
  displayName: 'VS Code',
  detect: { file: 'code', args: ['--version'] },
  open: { file: 'code', args: ['-n', '{folder}'] },
};

const SPECS: Readonly<Record<IdeAdapterKey, IdeAdapterSpec>> = {
  vscode: VS_CODE_ADAPTER,
};

export function ideAdapterSpec(key: string): IdeAdapterSpec | null {
  return isIdeAdapterKey(key) ? SPECS[key] : null;
}

export function ideOpenArgs(spec: IdeAdapterSpec, folder: string): { readonly file: string; readonly args: readonly string[] } {
  return {
    file: spec.open.file,
    args: spec.open.args.map((arg) => (arg === '{folder}' ? folder : arg)),
  };
}

export interface IdeAdapterStatus {
  readonly nodeId: string;
  readonly nodeName: string;
  readonly adapterKey: string;
  readonly displayName: string;
  readonly available: boolean;
}

export interface WorkspaceOpenTarget {
  readonly nodeId: string;
  readonly nodeName: string;
  readonly projectId: string;
  readonly projectName: string;
  readonly hasFolder: boolean;
  readonly nodeConnected: boolean;
}

export type IdeLaunchDecision =
  | {
    readonly action: 'open';
    readonly adapterKey: IdeAdapterKey;
    readonly displayName: string;
    readonly nodeId: string;
    readonly nodeName: string;
    readonly projectId: string;
  }
  | {
    readonly action: 'unavailable';
    readonly reason: 'no_host' | 'host_offline' | 'missing_adapter' | 'missing_ide' | 'missing_workspace' | 'wrong_project';
    readonly detail: string;
  };

export function decideIdeLaunch(input: {
  readonly nodeId: string | null;
  readonly nodeName: string | null;
  readonly projectId: string | null;
  readonly requestedAdapter?: string | null;
  readonly adapters: readonly IdeAdapterStatus[];
  readonly targets: readonly WorkspaceOpenTarget[];
}): IdeLaunchDecision {
  const adapterKey = input.requestedAdapter && isIdeAdapterKey(input.requestedAdapter)
    ? input.requestedAdapter
    : 'vscode';
  const spec = ideAdapterSpec(adapterKey)!;
  if (input.nodeId == null) {
    return { action: 'unavailable', reason: 'no_host', detail: 'IDE launch unavailable: no execution host is attached to this Worker.' };
  }
  const nodeName = input.nodeName?.trim() || 'this execution host';
  const adapters = input.adapters.filter((row) => row.nodeId === input.nodeId && row.adapterKey === adapterKey);
  if (adapters.length === 0) {
    return { action: 'unavailable', reason: 'missing_adapter', detail: `IDE launch unavailable on ${nodeName}.` };
  }
  const available = adapters.some((row) => row.available);
  if (!available) {
    return { action: 'unavailable', reason: 'missing_ide', detail: `${spec.displayName} is not installed on ${nodeName}.` };
  }
  if (input.projectId == null) {
    return { action: 'unavailable', reason: 'missing_workspace', detail: `No project/worktree is bound on ${nodeName}.` };
  }
  const target = input.targets.find((row) => row.nodeId === input.nodeId && row.projectId === input.projectId);
  if (target == null) {
    return { action: 'unavailable', reason: 'wrong_project', detail: `That project is not bound on ${nodeName}.` };
  }
  if (!target.nodeConnected) {
    return { action: 'unavailable', reason: 'host_offline', detail: `IDE launch unavailable: ${nodeName} is not reporting in.` };
  }
  if (!target.hasFolder) {
    return { action: 'unavailable', reason: 'missing_workspace', detail: `No worktree folder is recorded for this project on ${nodeName}.` };
  }
  return {
    action: 'open',
    adapterKey,
    displayName: spec.displayName,
    nodeId: input.nodeId,
    nodeName,
    projectId: input.projectId,
  };
}
